import { isBring, isRunningTests, useSetBackgroundColor } from '@glow/common'
import { ErrorBoundary } from '@glow/common-components'
import { siteAnalyticsTrackPageViewDynamically, siteMetricsSetUser } from '@glow/instrumentation'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import i18n from 'i18next'
import { Suspense, useEffect, useRef } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { HelmetProvider } from 'react-helmet-async'
import { I18nextProvider } from 'react-i18next'
import { Provider, useSelector } from 'react-redux'
import { BrowserRouter, useLocation, useNavigate } from 'react-router'
import { ROUTER_LOCATION_CHANGE_ACTION } from '../actions/actionTypes'
import { AppMenuProvider } from '../contexts/AppMenuContext'
import { ToastProvider } from '../contexts/ToastContext'
import { useAppDispatch } from '../reducers/redux-hooks'
import { store } from '../reducers/store'
import { GlobalStyle } from '../styles/global'
import { basePath } from '../utils/appBase'
import { AppStateType } from '../utils/appStateReduxStore'
import { getCustomerPortalLink } from '../utils/customerPortalUtils'
import { isAuthPath, isOpenPath } from '../utils/loginUtils'
import { ROLE_CUSTOMER } from '../utils/roles'
import { AppRoutes } from './AppRoutes'
import { AiChat } from './ai/AiChat'
import { MetaTags } from './meta-tags'

const queryClient = new QueryClient({})

export function App() {
  useSetBackgroundColor()

  return (
    <DndProvider backend={HTML5Backend}>
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <BrowserRouter basename={basePath() || undefined}>
            <Suspense fallback={null}>
              <QueryClientProvider client={queryClient}>
                <LocationObserver>
                  <AuthRedirector>
                    <HelmetProvider>
                      <MetaTags />
                      <ErrorBoundary
                        isRunningTests={isRunningTests()}
                        title={i18n.t('error.title')}
                        buttonText={i18n.t('error.errorClickToReload')}
                      >
                        <ToastProvider>
                          <AppMenuProvider>
                            <AppRoutes />
                            <AiChat />
                          </AppMenuProvider>
                        </ToastProvider>
                      </ErrorBoundary>
                    </HelmetProvider>
                  </AuthRedirector>
                </LocationObserver>
                <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
              </QueryClientProvider>
            </Suspense>
            <GlobalStyle />
          </BrowserRouter>
        </Provider>
      </I18nextProvider>
    </DndProvider>
  )
}

const LocationObserver = ({ children }: { children: any }) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const previousLocation = useRef('')

  useEffect(() => {
    dispatch({ type: ROUTER_LOCATION_CHANGE_ACTION })

    const url = window.location.origin + location.pathname + location.search
    siteAnalyticsTrackPageViewDynamically({
      pageUrl: url,
      pageTitle: document.title,
      previousUrl: previousLocation.current
    })

    previousLocation.current = url
  }, [location.pathname])

  return children
}

const AuthRedirector = ({ children }: { children: any }) => {
  const user = useSelector((state: AppStateType) => state.get('user'))
  const location = useLocation()
  const navigate = useNavigate()
  const userId = user.get('id')

  useEffect(() => {
    siteMetricsSetUser(userId)
  }, [userId])

  useEffect(() => {
    if (
      isBring() &&
      user.get('role') === ROLE_CUSTOMER &&
      location.pathname.indexOf('/customer') === -1 &&
      !isOpenPath()
    ) {
      window.location.href = getCustomerPortalLink()
    } else if (user.get('requiresEmail') && !isAuthPath()) {
      navigate('/auth/set-email')
    } else if (user.get('requiresPwd') && !isAuthPath()) {
      navigate('/auth/request-password')
    }
  }, [user.get('requiresEmail'), user.get('requiresPwd'), user.get('role'), location.pathname])

  return children
}
